/**
 * Phase 162 — Per-Player Longitudinal Progression Chart
 *
 * Renders a multi-metric scouting radar for a single jersey number across
 * all historical Player-focused video analyses. Coaches/scouts toggle which
 * KPI lines (xG, Pass Accuracy, Tackles Won, Possession) overlay the same
 * chronological X-axis, building an undeniable visual narrative of player
 * progression or performance dips over a season.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  TrendingUp, Hash, Trophy, Target, Activity, Shield,
  ChevronDown, Sparkles, Loader2, Share2, Copy, Check, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import ScoutHeatmapTile from '@/components/admin/ScoutHeatmapTile';

const METRICS = [
  { key: 'xg',            label: 'xG',            color: '#a855f7', icon: Target,    yMax: 3 },
  { key: 'pass_accuracy', label: 'Pass Acc %',    color: '#3b82f6', icon: Activity,  yMax: 100 },
  { key: 'tackles_won',   label: 'Tackles Won %', color: '#ef4444', icon: Shield,    yMax: 100 },
  { key: 'possession',    label: 'Possession %',  color: '#10b981', icon: Trophy,    yMax: 100 },
];

export default function PlayerProgressionChart({ defaultJersey = '' }) {
  const [roster, setRoster] = useState([]);
  const [jersey, setJersey] = useState(defaultJersey);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeMetrics, setActiveMetrics] = useState({
    xg: true, pass_accuracy: true, tackles_won: false, possession: false,
  });

  // Phase 168.d — landing ping. Fires once per session if the page is loaded
  // with `?utm_source=weekly_digest`, attributing the click back to the most
  // recent Phase 167 digest for the academy_owner.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if ((params.get('utm_source') || '').toLowerCase() !== 'weekly_digest') return;
    if (window.sessionStorage.getItem('phase168d_landing_pinged') === '1') return;
    window.sessionStorage.setItem('phase168d_landing_pinged', '1');
    api.post('/admin/scouting/digest/landing-ping', {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      path: window.location.pathname + window.location.search,
      referrer: document.referrer || '',
    }).catch(() => { /* non-fatal — attribution is best-effort */ });
  }, []);
  // Phase 164 — share link state
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [exposure, setExposure] = useState(null);

  // Roster load — first jersey auto-selected if none passed in
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/video-analysis/scouting-roster');
        setRoster(r.data?.roster || []);
        if (!defaultJersey && r.data?.roster?.length) {
          setJersey(r.data.roster[0].jersey_number);
        }
      } catch (_e) { /* silent */ }
    })();
  }, [defaultJersey]);

  // Timeline load — refires whenever jersey changes
  const loadTimeline = useCallback(async () => {
    if (!jersey) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/video-analysis/player-kpis?jersey_number=${encodeURIComponent(jersey)}`);
      setData(r.data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [jersey]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  // Phase 164 — load exposure tile for the selected jersey
  useEffect(() => {
    if (!jersey) { setExposure(null); return; }
    (async () => {
      try {
        const r = await api.get(`/admin/scout/exposure/${encodeURIComponent(jersey)}`);
        setExposure(r.data);
      } catch (_e) { setExposure(null); }
    })();
  }, [jersey]);

  // Phase 164 — generate a public scout link
  const handleGenerateShare = async () => {
    if (!jersey) return;
    setSharing(true);
    setShareUrl('');
    setShareCopied(false);
    try {
      const r = await api.post('/admin/scout/generate-link', { jersey_number: jersey, expires_days: 30 });
      setShareUrl(r.data?.public_url || '');
      toast.success('🔗 Share link generated — 30-day expiry.');
    } catch (e) {
      toast.error('Failed to generate link', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSharing(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      toast.success('Share link copied to clipboard');
      setTimeout(() => setShareCopied(false), 2200);
    } catch (_e) { /* clipboard may be blocked */ }
  };

  // Recharts data: flatten kpis → top-level fields per metric
  const chartRows = useMemo(() => {
    if (!data?.timeline) return [];
    return data.timeline.map((p, i) => ({
      idx: i + 1,
      date: p.date,
      label: p.date,
      xg: p.kpis.xg,
      pass_accuracy: p.kpis.pass_accuracy,
      tackles_won: p.kpis.tackles_won,
      possession: p.kpis.possession,
      match_title: p.match_title,
    }));
  }, [data]);

  const toggleMetric = (k) =>
    setActiveMetrics((p) => ({ ...p, [k]: !p[k] }));

  return (
    <Card className="border-purple-200/60 bg-gradient-to-br from-purple-50/30 via-white to-violet-50/20"
      data-testid="phase162-progression-chart">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-semibold text-base sm:text-lg" data-testid="phase162-title">
                  Player Progression Radar
                </h2>
                <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[10px] font-bold uppercase tracking-wider">Phase 162</Badge>
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold uppercase tracking-wider">
                  <Sparkles className="h-2.5 w-2.5 mr-1" /> Legend Tier
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="phase162-subtitle">
                Longitudinal scouting — toggle KPI lines for the selected jersey across every Player-focused analysis on file.
              </p>
            </div>
          </div>

          {/* Jersey picker */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Hash className="h-4 w-4 text-purple-600" />
            <div className="relative">
              <select
                value={jersey}
                onChange={(e) => setJersey(e.target.value)}
                disabled={!roster.length}
                data-testid="phase162-jersey-select"
                className="appearance-none pl-3 pr-8 py-2 text-sm font-semibold bg-white border border-purple-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-600 disabled:opacity-50"
              >
                {roster.length === 0 && <option value="">No player data yet</option>}
                {roster.map((r) => (
                  <option key={`${r.jersey_number}-${r.sport}`} value={r.jersey_number}>
                    {r.jersey_number} · {r.sport} · {r.match_count} matches
                  </option>
                ))}
              </select>
              <ChevronDown className="h-3.5 w-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            {/* Phase 164 — Share Scout Profile */}
            <Button
              variant="outline" size="sm" disabled={!jersey || sharing}
              onClick={handleGenerateShare}
              className="h-9 rounded-lg text-xs font-semibold gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="phase164-share-btn"
            >
              <Share2 className="h-3.5 w-3.5" />
              {sharing ? 'Minting…' : 'Share Scout Link'}
            </Button>
          </div>
        </div>

        {/* Phase 164 — generated share URL + exposure tile */}
        {(shareUrl || exposure) && (
          <div className="flex flex-wrap items-stretch gap-2" data-testid="phase164-share-row">
            {shareUrl && (
              <div className="flex-1 min-w-0 bg-white border border-purple-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="phase164-share-url-row">
                <Share2 className="h-3.5 w-3.5 text-purple-600 flex-shrink-0" />
                <code className="text-[11px] text-slate-700 truncate flex-1" data-testid="phase164-share-url">{shareUrl}</code>
                <button
                  type="button" onClick={copyShareLink}
                  className="text-[11px] font-bold text-purple-700 hover:text-purple-900 inline-flex items-center gap-1 flex-shrink-0"
                  data-testid="phase164-share-copy-btn"
                >
                  {shareCopied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                </button>
              </div>
            )}
            {exposure && (
              <div className="bg-white border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2"
                data-testid="phase164-exposure-tile">
                <Eye className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                <div className="text-[11px] leading-tight">
                  <div className="font-bold text-slate-800">
                    {exposure.views_30d} <span className="text-slate-500 font-normal">scout views (30d)</span>
                  </div>
                  <div className="text-slate-500">
                    {exposure.views_7d} this week · {exposure.total_views} total
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase 166 — Scout Network Heatmap (per-city engagement) */}
        {jersey && <ScoutHeatmapTile jersey={jersey} />}

        {/* Metric toggle pills */}
        <div className="flex flex-wrap gap-1.5" data-testid="phase162-metric-toggles">
          {METRICS.map((m) => {
            const Icon = m.icon;
            const on = activeMetrics[m.key];
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => toggleMetric(m.key)}
                data-testid={`phase162-metric-toggle-${m.key}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all ${
                  on
                    ? 'text-white shadow-sm'
                    : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
                style={on ? { background: m.color, borderColor: m.color } : {}}
              >
                <Icon className="h-3 w-3" /> {m.label}
              </button>
            );
          })}
        </div>

        {/* Averages strip */}
        {data?.averages && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="phase162-averages-strip">
            {METRICS.map((m) => (
              <div key={m.key} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5"
                data-testid={`phase162-avg-${m.key}`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <span style={{ color: m.color }}>●</span> Avg {m.label}
                </div>
                <div className="text-lg font-bold text-slate-800 leading-tight">
                  {data.averages[m.key] ?? '—'}
                  {m.key !== 'xg' && '%'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 min-h-[300px]" data-testid="phase162-chart-canvas">
          {loading && (
            <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Rendering scouting radar…
            </div>
          )}
          {!loading && (!data || data.data_points_count === 0) && (
            <div className="h-72 flex flex-col items-center justify-center gap-2 text-center px-6">
              <TrendingUp className="h-8 w-8 text-slate-300" />
              <div className="text-sm font-semibold text-slate-700">No Player-focused complete analyses yet for {jersey || 'this jersey'}.</div>
              <div className="text-xs text-slate-500 max-w-md">Create a Video Analysis with Analysis Boundary = "Individual Player Tracking" and a target jersey number to start building this player's progression timeline.</div>
            </div>
          )}
          {!loading && data && data.data_points_count > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartRows} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
                  labelFormatter={(label, payload) => {
                    const row = payload?.[0]?.payload;
                    return row ? `${row.match_title} · ${row.date}` : label;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {METRICS.filter((m) => activeMetrics[m.key]).map((m) => (
                  <Line
                    key={m.key}
                    type="monotone"
                    dataKey={m.key}
                    name={m.label}
                    stroke={m.color}
                    strokeWidth={2.5}
                    dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Match list footer */}
        {data && data.data_points_count > 0 && (
          <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1"
            data-testid="phase162-footer">
            <span><strong className="text-slate-700">{data.data_points_count}</strong> matches on file</span>
            <span>· latest: <strong className="text-slate-700">{chartRows[chartRows.length - 1]?.match_title}</strong></span>
            <span>· jersey: <strong className="text-slate-700">{data.jersey_number}</strong></span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
